/**
 * Dragan Pod split-shipment cart option.
 * Uses Shopify Ajax Cart APIs and the theme pubsub cartUpdate event.
 * Safe to re-init after section reloads (custom element + singleton controller).
 */
(function () {
  const SOURCE = 'split-shipment';
  const PLACEHOLDER_HANDLE = 'REPLACE_WITH_POD_HANDLE';
  const PLACEHOLDER_FEE = 'REPLACE_WITH_FEE_VARIANT_ID';

  function getConfig() {
    return window.DraganSplitShipment || {};
  }

  function isConfigured() {
    const config = getConfig();
    return Boolean(config.enabled && config.feeVariantId && config.feeVariantId !== PLACEHOLDER_FEE);
  }

  function fetchConfigJSON() {
    return {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    };
  }

  function readStoredPodIds() {
    try {
      const raw = sessionStorage.getItem('draganSplitShipmentPodIds');
      return raw ? JSON.parse(raw) : [];
    } catch (e) {
      return [];
    }
  }

  function mergeKnownPodIds(ids) {
    const config = getConfig();
    const current = Array.isArray(config.knownPodProductIds) ? config.knownPodProductIds : [];
    const next = new Set(
      [...current, ...readStoredPodIds(), ...(ids || [])]
        .map((id) => String(id || '').trim())
        .filter(Boolean)
    );
    config.knownPodProductIds = Array.from(next);
    window.DraganSplitShipment = config;
    try {
      sessionStorage.setItem('draganSplitShipmentPodIds', JSON.stringify(config.knownPodProductIds));
    } catch (e) {
      /* ignore private mode */
    }
  }

  function isPodItem(item, config) {
    if (!item) return false;
    const productId = String(item.product_id || '');
    const handle = item.handle || '';

    if (config.podHandle && config.podHandle !== PLACEHOLDER_HANDLE && handle === config.podHandle) {
      return true;
    }
    if (config.podProductId && productId === String(config.podProductId)) {
      return true;
    }
    if (Array.isArray(config.knownPodProductIds) && config.knownPodProductIds.map(String).includes(productId)) {
      return true;
    }
    return false;
  }

  function isFeeItem(item, config) {
    return String(item.variant_id) === String(config.feeVariantId);
  }

  function isOtherPhysical(item, config) {
    if (isFeeItem(item, config) || isPodItem(item, config)) return false;
    if (item.gift_card) return false;
    return Boolean(item.requires_shipping);
  }

  function analyzeCart(cart) {
    const config = getConfig();
    const items = cart.items || [];
    let hasPod = false;
    let hasOtherPhysical = false;
    const feeItems = [];
    let feeQuantity = 0;
    const podIds = [];

    items.forEach((item) => {
      if (isFeeItem(item, config)) {
        feeItems.push(item);
        feeQuantity += item.quantity;
        return;
      }
      if (isPodItem(item, config)) {
        hasPod = true;
        podIds.push(item.product_id);
        return;
      }
      if (isOtherPhysical(item, config)) {
        hasOtherPhysical = true;
      }
    });

    mergeKnownPodIds(podIds);

    return {
      eligible: hasPod && hasOtherPhysical,
      hasPod,
      hasOtherPhysical,
      feeItems,
      feeQuantity,
      hasFee: feeQuantity > 0,
      attribute: (cart.attributes && cart.attributes[config.attributeName]) || '',
    };
  }

  class SplitShipmentController {
    constructor() {
      this.updating = false;
      this.queued = false;
      this.checkoutSyncing = false;
      this.initialized = false;
      this.boundCheckout = this.onCheckoutClick.bind(this);
      this.unsubscribe = null;
    }

    init() {
      if (!getConfig().enabled) return;
      if (!this.unsubscribe && typeof subscribe === 'function') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (!event || event.source === SOURCE) return;
          this.scheduleReconcile();
        });
      }
      this.bindCheckoutButtons();
      this.scheduleReconcile();

      if (!this.initialized) {
        this.initialized = true;
        document.addEventListener('shopify:section:load', () => {
          this.bindCheckoutButtons();
          this.scheduleReconcile();
        });
      }
    }

    scheduleReconcile() {
      if (this.updating) {
        this.queued = true;
        return;
      }
      this.reconcile();
    }

    bindCheckoutButtons() {
      document
        .querySelectorAll(
          '#checkout, #CartDrawer-Checkout, #cart-notification-form [name="checkout"], button[name="checkout"]'
        )
        .forEach((button) => {
          button.removeEventListener('click', this.boundCheckout);
          button.addEventListener('click', this.boundCheckout);
        });
    }

    async onCheckoutClick(event) {
      if (!getConfig().enabled || this.checkoutSyncing) return;

      try {
        const cart = await this.getCart();
        const state = analyzeCart(cart);

        if (!state.eligible && !state.hasFee) return;

        if (!isConfigured()) {
          if (state.hasFee) {
            // Fee present but settings incomplete — still allow checkout.
            return;
          }
          return;
        }

        if (!state.eligible && state.hasFee) {
          event.preventDefault();
          event.stopPropagation();
          await this.reconcile();
          return;
        }

        const uiChoice = this.getDominantUiChoice();
        const shouldSplit = uiChoice === 'split' || (!uiChoice && state.hasFee);
        const matches = shouldSplit ? state.hasFee && state.feeQuantity === 1 : !state.hasFee;
        const attributeOk = shouldSplit
          ? state.attribute === getConfig().attributeSplit
          : state.attribute === getConfig().attributeTogether || state.attribute === '';

        if (matches && attributeOk) return;

        event.preventDefault();
        event.stopPropagation();
        this.checkoutSyncing = true;
        await this.applyChoice(shouldSplit ? 'split' : 'together', { fromCheckout: true });

        const form = event.target.closest('form');
        if (form) {
          if (typeof form.requestSubmit === 'function') form.requestSubmit();
          else form.submit();
        }
      } catch (error) {
        console.error('[split-shipment] checkout sync failed', error);
        this.setAllErrors(getConfig().errorText || 'We could not update your shipping preference. Please try again.');
      } finally {
        this.checkoutSyncing = false;
      }
    }

    getDominantUiChoice() {
      const visible = Array.from(document.querySelectorAll('split-shipment-option')).filter(
        (el) => !el.hasAttribute('hidden')
      );
      for (const el of visible) {
        const checked = el.querySelector('input[type="radio"]:checked');
        if (checked) return checked.value;
      }
      return null;
    }

    async getCart() {
      const cartUrl = `${(getConfig().routes && getConfig().routes.cart) || '/cart'}.js`;
      const response = await fetch(cartUrl, {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error('Failed to load cart');
      return response.json();
    }

    setAllLoading(isLoading) {
      document.querySelectorAll('split-shipment-option').forEach((el) => {
        el.classList.toggle('is-loading', isLoading);
        el.querySelectorAll('input[type="radio"]').forEach((input) => {
          input.disabled = isLoading;
        });
      });
    }

    setAllErrors(message) {
      document.querySelectorAll('split-shipment-option').forEach((el) => {
        const error = el.querySelector('.split-shipment__error');
        if (!error) return;
        if (message) {
          error.textContent = message;
          error.hidden = false;
        } else {
          error.textContent = '';
          error.hidden = true;
        }
      });
    }

    syncUi(state) {
      document.querySelectorAll('split-shipment-option').forEach((el) => {
        if (state.eligible) el.removeAttribute('hidden');
        else el.setAttribute('hidden', '');

        const splitInput = el.querySelector('input[value="split"]');
        const togetherInput = el.querySelector('input[value="together"]');
        if (!splitInput || !togetherInput) return;

        const wantSplit = state.eligible && state.hasFee;
        splitInput.checked = wantSplit;
        togetherInput.checked = !wantSplit;
      });
    }

    ensureJsRoute(url, fallback) {
      const value = url || fallback;
      if (!value) return fallback;
      if (/\.js(\?|$)/.test(value)) return value;
      return value.includes('?') ? value.replace('?', '.js?') : `${value}.js`;
    }

    async parseJsonResponse(response, fallbackMessage) {
      const text = await response.text();
      let data;
      try {
        data = text ? JSON.parse(text) : {};
      } catch (e) {
        throw new Error(fallbackMessage || 'Unexpected cart response');
      }
      return data;
    }

    getSectionsPayload() {
      const sections = [];
      if (document.querySelector('cart-drawer') || document.getElementById('CartDrawer')) {
        sections.push('cart-drawer');
      }
      const mainItems = document.getElementById('main-cart-items');
      if (mainItems) sections.push(mainItems.dataset.id || 'main-cart-items');
      const mainFooter = document.getElementById('main-cart-footer');
      if (mainFooter) sections.push(mainFooter.dataset.id || 'main-cart-footer');
      if (document.getElementById('shopify-section-cart-icon-bubble') || document.getElementById('cart-icon-bubble')) {
        sections.push('cart-icon-bubble');
      }
      return Array.from(new Set(sections));
    }

    async postCart(url, body) {
      const response = await fetch(url, {
        ...fetchConfigJSON(),
        body: JSON.stringify(body),
      });
      const data = await this.parseJsonResponse(response, 'Cart request failed');
      if (!response.ok || (data.status && data.message && !data.items && !data.token)) {
        throw new Error(data.description || data.message || 'Cart request failed');
      }
      return data;
    }

    /**
     * One-request cart update: line quantities + attributes + section HTML.
     */
    async updateCart(payload) {
      const config = getConfig();
      const sections = this.getSectionsPayload();
      const body = {
        ...payload,
        sections,
        sections_url: (config.routes && config.routes.cart) || window.location.pathname || '/cart',
      };
      return this.postCart(this.ensureJsRoute(config.routes.cartUpdate, '/cart/update.js'), body);
    }

    /**
     * Add fee + set attribute + return section HTML in as few round-trips as possible.
     */
    async addFeeWithSections(attributeValue) {
      const config = getConfig();
      const sections = this.getSectionsPayload();
      const variantId = String(config.feeVariantId).trim();
      const sectionsUrl = (config.routes && config.routes.cart) || window.location.pathname || '/cart';
      const addUrl = this.ensureJsRoute(config.routes.cartAdd, '/cart/add.js');

      let addData;
      try {
        addData = await this.postCart(addUrl, {
          items: [{ id: variantId, quantity: 1 }],
          attributes: {
            [config.attributeName]: attributeValue,
          },
          sections,
          sections_url: sectionsUrl,
        });
      } catch (error) {
        // Some shop configs reject attributes on /cart/add.js — retry without them.
        addData = await this.postCart(addUrl, {
          items: [{ id: variantId, quantity: 1 }],
          sections,
          sections_url: sectionsUrl,
        });
      }

      const attrs = addData.attributes || {};
      if (attrs[config.attributeName] !== attributeValue) {
        await this.updateAttributesOnly(attributeValue);
      }

      return addData;
    }

    /**
     * Lightweight attribute-only update (no section re-render).
     */
    async updateAttributesOnly(value) {
      const config = getConfig();
      return this.postCart(this.ensureJsRoute(config.routes.cartUpdate, '/cart/update.js'), {
        attributes: {
          [config.attributeName]: value,
        },
      });
    }

    async updateAttributes(value) {
      return this.updateAttributesOnly(value);
    }

    async changeLine(key, quantity) {
      const config = getConfig();
      return this.postCart(this.ensureJsRoute(config.routes.cartChange, '/cart/change.js'), {
        id: key,
        quantity,
      });
    }

    async addFee() {
      return this.addFeeWithSections(getConfig().attributeSplit);
    }

    replaceHtml(target, html) {
      if (!target || typeof html !== 'string') return;
      target.innerHTML = html;
    }

    finishUiFromResponse(data, stateOverrides = {}) {
      if (data && data.sections) {
        this.applySections(data.sections);
      }

      // /cart/update.js returns a full cart; /cart/add.js often returns only added lines.
      const isFullCart = Boolean(data && (typeof data.item_count === 'number' || data.token));
      if (isFullCart && data.items) {
        this.syncUi({ ...analyzeCart(data), ...stateOverrides });
      } else if (Object.keys(stateOverrides).length) {
        this.syncUi(stateOverrides);
      }

      this.bindCheckoutButtons();

      const drawer = document.querySelector('cart-drawer');
      if (drawer && drawer.classList.contains('active')) {
        document.body.classList.add('overflow-hidden');
      }
    }

    async refreshCartUI() {
      const drawer = document.querySelector('cart-drawer');
      const drawerWasOpen = Boolean(drawer && drawer.classList.contains('active'));
      const sections = this.getSectionsPayload();

      if (sections.length) {
        const sectionResponse = await fetch(
          `${getConfig().routes.cart || '/cart'}?sections=${sections.join(',')}`
        );
        if (sectionResponse.ok) {
          const htmlMap = await sectionResponse.json();
          this.applySections(htmlMap);
        }
      }

      if (drawerWasOpen) {
        const openDrawer = document.querySelector('cart-drawer');
        if (openDrawer) {
          openDrawer.classList.add('animate', 'active');
          document.body.classList.add('overflow-hidden');
        }
      }

      this.bindCheckoutButtons();
    }

    applySections(htmlMap) {
      if (!htmlMap) return;

      Object.keys(htmlMap).forEach((sectionId) => {
        const html = htmlMap[sectionId];
        if (!html) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');

        if (sectionId === 'cart-drawer' || String(sectionId).includes('cart-drawer')) {
          const targetInner = document.querySelector('#CartDrawer .drawer__inner');
          const sourceInner = doc.querySelector('#CartDrawer .drawer__inner') || doc.querySelector('.drawer__inner');
          if (targetInner && sourceInner) {
            this.replaceHtml(targetInner, sourceInner.innerHTML);
          }
          return;
        }

        if (sectionId === 'cart-icon-bubble' || String(sectionId).includes('cart-icon-bubble')) {
          const target =
            document.getElementById('shopify-section-cart-icon-bubble') || document.getElementById('cart-icon-bubble');
          const source = doc.querySelector('.shopify-section') || doc.body;
          if (target && source) this.replaceHtml(target, source.innerHTML);
          return;
        }

        const footerHostSource = doc.querySelector('[data-split-shipment-cart-host]');
        const footerHostTarget = document.querySelector('[data-split-shipment-cart-host]');
        if (footerHostTarget && footerHostSource) {
          footerHostTarget.outerHTML = footerHostSource.outerHTML;
        }

        if (doc.querySelector('#main-cart-items')) {
          const itemsTarget = document.querySelector('#main-cart-items .js-contents');
          const itemsSource = doc.querySelector('#main-cart-items .js-contents');
          if (itemsTarget && itemsSource) this.replaceHtml(itemsTarget, itemsSource.innerHTML);
        }

        if (doc.querySelector('#main-cart-footer')) {
          const footerJsTarget = document.querySelector('#main-cart-footer .js-contents');
          const footerJsSource = doc.querySelector('#main-cart-footer .js-contents');
          if (footerJsTarget && footerJsSource) this.replaceHtml(footerJsTarget, footerJsSource.innerHTML);
        }
      });
    }

    async reconcile() {
      if (!getConfig().enabled) return;
      if (this.updating) {
        this.queued = true;
        return;
      }

      this.updating = true;
      this.setAllErrors('');

      try {
        let cart = await this.getCart();
        let state = analyzeCart(cart);
        let lineItemsChanged = false;
        const config = getConfig();

        if (!isConfigured()) {
          this.syncUi(state);
          return;
        }

        if (state.feeQuantity > 1 || (state.hasFee && !state.eligible)) {
          const response = await this.updateCart({
            updates: { [String(config.feeVariantId)]: state.eligible && state.feeQuantity > 1 ? 1 : 0 },
            attributes: {
              [config.attributeName]:
                state.eligible && state.feeQuantity > 1 ? config.attributeSplit : config.attributeTogether,
            },
          });
          lineItemsChanged = true;
          cart = response;
          state = analyzeCart(cart);
          this.finishUiFromResponse(response);
        } else if (state.eligible && !state.hasFee && state.attribute !== config.attributeTogether) {
          // Attribute-only correction — no section re-fetch needed.
          cart = await this.updateAttributesOnly(config.attributeTogether);
          state = analyzeCart(cart);
        }

        if (!lineItemsChanged) this.syncUi(state);
      } catch (error) {
        console.error('[split-shipment] reconcile failed', error);
        this.setAllErrors(
          error && error.message
            ? error.message
            : getConfig().errorText || 'We could not update your shipping preference. Please try again.'
        );
      } finally {
        this.updating = false;
        if (this.queued) {
          this.queued = false;
          this.scheduleReconcile();
        }
      }
    }

    async applyChoice(choice, options = {}) {
      if (this.updating && !options.fromCheckout) return;

      this.updating = true;
      this.setAllLoading(true);
      this.setAllErrors('');

      try {
        if (!isConfigured()) {
          throw new Error('Split shipment fee variant is not configured');
        }

        const config = getConfig();
        const cart = await this.getCart();
        const state = analyzeCart(cart);

        if (!state.eligible && choice === 'split') {
          throw new Error('Cart is not eligible for split shipment');
        }

        let response;

        if (choice === 'split') {
          if (!state.hasFee) {
            // Typically 1 request (add + attribute + sections). Fallback adds a tiny attribute call.
            response = await this.addFeeWithSections(config.attributeSplit);
            this.finishUiFromResponse(response, {
              eligible: true,
              hasFee: true,
              feeQuantity: 1,
            });
          } else if (state.feeQuantity > 1) {
            response = await this.updateCart({
              updates: { [String(config.feeVariantId)]: 1 },
              attributes: { [config.attributeName]: config.attributeSplit },
            });
            this.finishUiFromResponse(response);
          } else {
            // Fee already present — only ensure attribute, no line-item churn.
            response = await this.updateAttributesOnly(config.attributeSplit);
            this.syncUi({ ...state, hasFee: true, attribute: config.attributeSplit });
          }
        } else {
          // Remove fee + set attribute + refresh sections in one request.
          response = await this.updateCart({
            updates: { [String(config.feeVariantId)]: 0 },
            attributes: { [config.attributeName]: config.attributeTogether },
          });
          this.finishUiFromResponse(response, {
            eligible: state.eligible,
            hasFee: false,
            feeQuantity: 0,
            attribute: config.attributeTogether,
          });
        }

        // Do not publish cartUpdate here: that would trigger a second full section fetch.
      } catch (error) {
        console.error('[split-shipment] applyChoice failed', error);
        this.setAllErrors(
          error && error.message
            ? error.message
            : getConfig().errorText || 'We could not update your shipping preference. Please try again.'
        );
        try {
          const cart = await this.getCart();
          this.syncUi(analyzeCart(cart));
        } catch (e) {
          /* ignore */
        }
        if (options.fromCheckout) throw error;
      } finally {
        this.setAllLoading(false);
        this.updating = false;
        if (this.queued) {
          this.queued = false;
          this.scheduleReconcile();
        }
      }
    }
  }

  window.DraganSplitShipmentController =
    window.DraganSplitShipmentController || new SplitShipmentController();

  if (!customElements.get('split-shipment-option')) {
    customElements.define(
      'split-shipment-option',
      class SplitShipmentOption extends HTMLElement {
        connectedCallback() {
          if (this._bound) return;
          this._bound = true;

          const known = (this.dataset.knownPodIds || '')
            .split(',')
            .map((id) => id.trim())
            .filter(Boolean);
          mergeKnownPodIds(known);

          this.addEventListener('change', (event) => {
            if (!(event.target instanceof HTMLInputElement)) return;
            if (event.target.type !== 'radio') return;
            window.DraganSplitShipmentController.applyChoice(event.target.value);
          });

          window.DraganSplitShipmentController.init();
        }
      }
    );
  }

  document.addEventListener('DOMContentLoaded', () => {
    if (getConfig().enabled) window.DraganSplitShipmentController.init();
  });

  if (document.readyState !== 'loading' && getConfig().enabled) {
    window.DraganSplitShipmentController.init();
  }
})();
